import { useApolloClient } from '@apollo/client'
import type { ProjectElementNode } from '@deltek/specification-client/core/Models/productListingDetail'
import { ProjectElementType } from '@deltek/specification-client/core/Models/projectElement'
import { GetProjectElementNodesForAEProjectWSQuery } from 'api/graphQL/queries/projectElements.queries'
import { useActionBarStore } from 'components/WorkspaceEditor/ActionBarStore'
import { useElementItemShallowSelector } from 'components/WorkspaceEditor/ElementItemStore'
import type { ContentItemViewNodesType } from 'components/WorkspaceEditor/types/contentItemViewNode.type'
import type { GetProjectElementNodesInput } from 'models/getProjectElementNodesInput'
import { useCallback } from 'react'
import { usePTCacheStore, usePTCacheStoreShallowSelector } from '../store'
import {
    buildCombinedPTElementNodes,
    extractTextBeforeColon,
    getPTElementNodes,
} from '../utils'

interface GetProjectElementNodesResponse {
    getProjectElementNodes?: {
        projectElementNodes?: ProjectElementNode[]
        totalCount?: number
    }
}

/**
 * Hook for fetching and caching Product Type nodes for a family.
 *
 * This hook provides functions to:
 * 1. Fetch ALL PTs for a family (ignoring "Added To Project" filter) and cache them
 * 2. Retrieve a PT from cache by name (case-insensitive)
 *
 * Used to solve: PT activation fails when "Added To Project" filter is ON
 * because elementList only contains elements added to project.
 *
 * @returns Object containing:
 *   - fetchAndCacheFamilyPTs: Function to fetch all PTs for a family and cache them
 *   - getPTFromCache: Function to retrieve PT from cache by name
 *
 * @example
 * const { fetchAndCacheFamilyPTs, getPTFromCache } = usePTProjectFilterCache()
 *
 * // When family is selected + filter is ON:
 * await fetchAndCacheFamilyPTs(familyId, projectId)
 *
 * // During PT activation:
 * const cachedPT = getPTFromCache(familyId, ptName)
 * if (cachedPT && !existsInElementList(cachedPT)) {
 *     addToElementList(cachedPT)
 * }
 */
export const usePTProjectFilterCache = () => {
    const client = useApolloClient()
    const { setCachedFamilyPTs } = usePTCacheStoreShallowSelector((state) => ({
        setCachedFamilyPTs: state.setCachedFamilyPTs,
    }))
    const elementItemValue = useElementItemShallowSelector(
        (state) => state.elementItemValue
    )
    /**
     * Fetches ALL Product Type nodes for a family and caches them.
     * Always fetches with onlyElementsAssignedToProject: false to get ALL PTs regardless of filter.
     *
     * @param familyId - The family ID to fetch PTs for
     * @param projectId - The project ID
     * @param options - Optional dynamic GraphQL parameters
     */
    const fetchAndCacheFamilyPTs = useCallback(
        async (
            familyId: string,
            projectId: string,
            options?: {
                isProductFamilyGroupView?: boolean
                isFromAEWorkSpace?: boolean
                skipChildNodeChecks?: boolean
            }
        ): Promise<void> => {
            try {
                const { data } = await client.query<
                    GetProjectElementNodesResponse,
                    GetProjectElementNodesInput
                >({
                    query: GetProjectElementNodesForAEProjectWSQuery,
                    variables: {
                        input: {
                            projectId,
                            parentNodeId: familyId,
                            limit: 1000,
                            skip: 0,
                            onlyElementsAssignedToProject: false, // ⚠️ KEY: Ignore filter, get ALL PTs
                            projectElementType: [
                                ProjectElementType.ASSEMBLY,
                                ProjectElementType.PRODUCTFAMILY,
                                ProjectElementType.PRODUCTTYPE,
                            ],
                            isProductFamilyGroupView:
                                options?.isProductFamilyGroupView ?? true,
                            isFromAEWorkSpace:
                                options?.isFromAEWorkSpace ?? true,
                            skipChildNodeChecks:
                                options?.skipChildNodeChecks ?? false,
                        },
                    },
                    fetchPolicy: 'network-only',
                })

                const nodes =
                    data?.getProjectElementNodes?.projectElementNodes || []

                // Filter to only PRODUCTTYPE nodes (should already be filtered by query, but be safe)
                const productTypes = nodes.filter(
                    (node) => node.elementType === 'PRODUCTTYPE'
                )

                setCachedFamilyPTs(familyId, productTypes)
            } catch (err) {}
        },
        [client, setCachedFamilyPTs]
    )

    /**
     * Retrieves a Product Type from cache by name (case-insensitive).
     * If familyId is provided, searches that family first for faster lookup.
     * Falls back to searching all cached families if not found or no familyId provided.
     *
     * @param ptName - The name of the Product Type to find
     * @param familyId - Optional family ID for targeted lookup (from elementItemValue.parentFamilyId)
     * @returns The matching ProjectElementNode or null if not found
     */
    const getPTFromCache = useCallback(
        (ptName: string, familyId?: string): ProjectElementNode | null => {
            const allCachedFamilies = usePTCacheStore.getState().cachedFamilyPTs

            if (
                !allCachedFamilies ||
                Object.keys(allCachedFamilies).length === 0
            ) {
                return null
            }

            // Case-insensitive name matching
            const normalizedSearchName = ptName.toLowerCase()

            // If familyId provided, search that family first (targeted lookup)
            if (familyId && allCachedFamilies[familyId]) {
                const cachedPTs = allCachedFamilies[familyId]
                const foundPT = cachedPTs.find((pt) => {
                    const ptNodeName = extractTextBeforeColon(pt.text || '')
                    return ptNodeName.toLowerCase() === normalizedSearchName
                })
                if (foundPT) {
                    return foundPT
                }
            }

            // Fallback: Search across all cached families
            for (const fId in allCachedFamilies) {
                if (fId === familyId) continue // Already checked above
                const cachedPTs = allCachedFamilies[fId]
                const foundPT = cachedPTs.find((pt) => {
                    const ptNodeName = extractTextBeforeColon(pt.text || '')
                    return ptNodeName.toLowerCase() === normalizedSearchName
                })
                if (foundPT) {
                    return foundPT
                }
            }

            return null
        },
        []
    )

    /**
     * Helper function to check cache and add PT to elementList if needed.
     * This handles the "Added To Project" filter scenario where PT activation fails
     * because elementList only contains elements added to project.
     *
     * @param ptName - The PT name extracted from paragraph text
     * @returns The cached PT node if found and added, null otherwise
     */
    const addPTFromCacheIfNeeded = useCallback(
        (ptName: string): ProjectElementNode | null => {
            // Read current store to avoid stale closure after async PT additions
            const currentElementList = useActionBarStore.getState().elementList
            if (!ptName || !currentElementList) {
                return null
            }

            // Check if PT already in elementList (fast path)
            const existingPT = currentElementList.find(
                (node) =>
                    node.elementType === 'PRODUCTTYPE' &&
                    extractTextBeforeColon(node.text || '').toLowerCase() ===
                        ptName.toLowerCase()
            )
            if (existingPT) {
                return null // Already in list
            }

            // Check cache for PT using currentFamilyId for efficient targeted search
            const cachedPT = getPTFromCache(
                ptName,
                elementItemValue.currentFamilyId
            )

            if (!cachedPT) {
                return null // Not in cache
            }

            // Return the cached PT for detection logic only
            return cachedPT
        },
        [getPTFromCache, elementItemValue.currentFamilyId]
    )

    /**
     * Gets PT element nodes with cache support for a specific content item
     * @param contentItem - Optional content item to check cache for
     * @param returnCachedPTOnly - If true, returns only the cached PT without building combined list
     * @returns PT element nodes including cached PTs
     */
    const getPTElementNodesWithCache = useCallback(
        (
            contentItem?: Partial<ContentItemViewNodesType>,
            returnCachedPTOnly = false
        ): ProjectElementNode[] => {
            let cachedPT: ProjectElementNode | null = null

            // Check cache if content item provided
            if (contentItem) {
                const paragraphText = contentItem.elements?.[0]?.text
                if (paragraphText && contentItem.textType === 'Paragraph') {
                    const ptName = extractTextBeforeColon(paragraphText)
                    if (ptName) {
                        cachedPT = addPTFromCacheIfNeeded(ptName)
                    }
                }
            }

            if (returnCachedPTOnly) {
                return cachedPT ? [cachedPT] : []
            }

            // Build combined element list, deduplicate, and return PT nodes
            // Read current store to avoid stale closure after async PT additions
            return buildCombinedPTElementNodes(
                useActionBarStore.getState().elementList || [],
                cachedPT ? [cachedPT] : []
            )
        },
        [addPTFromCacheIfNeeded]
    )

    /**
     * Gets PT element nodes with bulk cache support for multiple content items
     * Used by detectInactivePTs and detectInactiveParentPTs for bulk operations
     * @param contentItems - Array of content items to check cache for
     * @returns PT element nodes including all cached PTs from the items
     */
    const getPTElementNodesWithBulkCache = useCallback(
        (
            contentItems: Partial<ContentItemViewNodesType>[]
        ): ProjectElementNode[] => {
            // Collect all cached PTs from all content items
            const cachedPTs: ProjectElementNode[] = []
            contentItems.forEach((item) => {
                const cachedPT = getPTElementNodesWithCache(item, true)
                if (cachedPT.length > 0) {
                    cachedPTs.push(...cachedPT)
                }
            })

            // Build combined element list, deduplicate, and return PT nodes
            // Read current store to avoid stale closure after async PT additions
            return buildCombinedPTElementNodes(
                useActionBarStore.getState().elementList || [],
                cachedPTs
            )
        },
        [getPTElementNodesWithCache]
    )

    return {
        fetchAndCacheFamilyPTs,
        getPTFromCache,
        addPTFromCacheIfNeeded,
        getPTElementNodesWithCache,
        getPTElementNodesWithBulkCache,
    }
}
